import {
  liveSuggestionFor,
  normalizeLiveTimeframe,
} from "../redux/live-suggestion/liveSuggestionSlice";
import {
  coinsInQuestion,
  marketLabel,
  modeLabel,
  otherMarketLines,
  pageKind,
  questionScope,
} from "./guideFacts";

function base(symbol) {
  return String(symbol || "")
    .split("/")[0]
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

function roundPct(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return "";
  return `${number.toPrecision(2)}%`;
}

function assistantCall(item) {
  const side = String(item?.TRADE_TYPE || "").split("/")[0];
  if (!side) return { side: "", text: "no AI Assistant call" };
  if (side === "Hold") return { side, text: "Hold" };
  const score = Number(item?.SIGNAL_STRENGTH);
  const word = Number.isFinite(score) && score * 20 >= 50 ? "Strong" : "Weak";
  const strength = Number.isFinite(score) ? `, strength ${Math.round(score)} of 5` : "";
  return { side, text: `${word} ${side}${strength}` };
}

function liveSentence(live, timeframe) {
  const side = String(live?.prediction || "").split("/")[0];
  if (!live || !side) {
    return { side: "", text: `The ${timeframe} pivot has not come back yet.` };
  }
  if (side === "Hold") {
    return {
      side,
      text: `The ${timeframe} pivot is quiet, so there is no turn to add.`,
    };
  }
  const raw = Number(live.confidence);
  const percent = Number.isFinite(raw) ? (raw > 1 ? raw : raw * 100) : null;
  const word = percent != null && percent >= 75 ? "Strong" : "Weak";
  const confidence =
    percent != null ? ` with about ${Math.round(percent)}% confidence` : "";
  return {
    side,
    text: `The ${timeframe} pivot says ${word} ${side}${confidence}. That confidence is not a position weight.`,
  };
}

function agreement(boardSide, liveSide, timeframe) {
  if (liveSide !== "Buy" && liveSide !== "Sell") {
    return "The pivot is quiet on this timeframe, so the category call stands.";
  }
  if (boardSide === liveSide) {
    return "The category call and the pivot agree.";
  }
  if (boardSide === "Hold") {
    return `These disagree. The category call is Hold and the ${timeframe} pivot is ${liveSide}. For a longer hold, follow the assistant.`;
  }
  if (boardSide === "Buy" || boardSide === "Sell") {
    return `These disagree. The category call is ${boardSide} and the ${timeframe} pivot is ${liveSide}. For a longer hold, follow the assistant.`;
  }
  return "";
}

function livesForCoin(suggestions, coin) {
  const name = base(coin);
  const seen = new Set();
  const lines = [];
  (suggestions || []).forEach((item) => {
    if (base(item?.symbol) !== name) return;
    const timeframe = normalizeLiveTimeframe(item?.timeframe);
    if (seen.has(timeframe)) return;
    seen.add(timeframe);
    lines.push(liveSentence(item, timeframe).text);
  });
  return lines;
}

function horizonNote(question) {
  const text = String(question || "").toLowerCase();
  if (/\b(month|months|year|years|long-term|long term|invest|portfolio)\b/.test(text)) {
    return "Horizon: months or a portfolio. Follow the category call. The 1d pivot is only a daily turn check. If they disagree, do not add the coin from the pivot. A 15m or 1h pivot is a shorter turn, not the assistant.";
  }
  if (/\b(week|weeks|swing)\b/.test(text)) {
    return "Horizon: a swing of days. Read the category call with the 1h and 4h pivot. The pivot looks for a turn. It is not the category call.";
  }
  return "";
}

function assistantLine(item, suggestions, timeframe, coefficients, cardTimeframe) {
  const coin = base(item?.SYMBOL_NAME);
  const call = assistantCall(item);
  const loaded = livesForCoin(suggestions, item?.SYMBOL_NAME);
  const selected = liveSentence(
    liveSuggestionFor(suggestions, item?.SYMBOL_NAME, timeframe),
    timeframe,
  );
  const liveCall = loaded.length ? loaded.join(" ") : selected.text;
  const frame = item?.MT_SLTP?.[cardTimeframe] || {};
  const stop = roundPct(
    Number(frame.STOP_LOSS_PERCENT || item?.STOP_LOSS_PERCENT) *
      (Number(coefficients?.stop_loss) || 1),
  );
  const target = roundPct(
    Number(frame.TAKE_PROFIT_PERCENT || item?.TAKE_PROFIT_PERCENT) *
      (Number(coefficients?.take_profit) || 1),
  );
  const size = Number(item?.SUGGESTED_AMOUNT_PERCENTAGE);
  const price = Number(item?.CURRENT_PRICE);
  const info = item?.CATEGORY_INFO || {};
  const bits = [`${coin}: the AI Assistant says ${call.text}.`];
  if (stop) bits.push(`Stop on the ${cardTimeframe} card is ${stop}.`);
  if (target) bits.push(`Target on the ${cardTimeframe} card is ${target}.`);
  if (Number.isFinite(size)) {
    bits.push(
      `The size on this card is ${(size * 100).toFixed(2)}% of this one trade, not a share of the whole account.`,
    );
  }
  if (Number.isFinite(price) && price > 0) bits.push(`Price on the card is ${price}.`);
  if (info.name || info.description) {
    const detail = String(info.description || "").replace(/\s+/g, " ").slice(0, 180);
    bits.push(
      `It is grouped as ${info.name || info.category || "a board category"}${detail ? `: ${detail}` : "."}`,
    );
  }
  bits.push(liveCall);
  const together = agreement(call.side, selected.side, timeframe);
  if (together) bits.push(together);
  return {
    coin,
    side: call.side,
    strength: Number(item?.SIGNAL_STRENGTH) || 0,
    live: selected.side || "",
    size: Number.isFinite(size) ? Math.round(size * 1000) / 10 : null,
    text: bits.join(" "),
  };
}

function insightSummary(item) {
  const votes = [];
  Object.entries(item || {}).forEach(([name, payload]) => {
    if (name === "coin" || !payload || typeof payload !== "object" || !payload.suggestion) return;
    if (payload.data_gap) return;
    const raw = String(payload.suggestion).split("/")[0].toLowerCase();
    const side = raw === "buy" ? "Buy" : raw === "sell" ? "Sell" : raw === "hold" ? "Hold" : "";
    if (!side) return;
    const score = Number(payload["signal strength"]);
    votes.push({
      label: payload.other_info?.name || name,
      category: payload.other_info?.category || "",
      side,
      strength: Number.isFinite(score) ? score : 0,
    });
  });
  const limited = votes.slice(0, 6);
  const buy = limited.filter((vote) => vote.side === "Buy").length;
  const sell = limited.filter((vote) => vote.side === "Sell").length;
  const hold = limited.length - buy - sell;
  const side = buy >= sell && buy >= hold && buy ? "Buy" : sell >= buy && sell >= hold && sell ? "Sell" : "Hold";
  const strength = limited.length
    ? limited.reduce((sum, vote) => sum + vote.strength, 0) / limited.length
    : 0;
  return { side, strength, bits: limited };
}

function insightLine(item, suggestions, timeframe) {
  const coin = base(item?.coin);
  const summary = insightSummary(item);
  const live = liveSuggestionFor(suggestions, item?.coin, timeframe);
  const liveCall = liveSentence(live, timeframe);
  const detail = summary.bits.length
    ? summary.bits
        .map((vote) => {
          const strength = vote.strength ? ` (${Math.round(vote.strength)} of 5)` : "";
          const family = vote.category ? `${vote.category}: ` : "";
          return `${family}${vote.label} says ${vote.side}${strength}`;
        })
        .join("; ")
    : "no indicator calls";
  return {
    coin,
    side: summary.side,
    strength: summary.strength,
    live: liveCall.side || "",
    text: `${coin} on Insights: ${detail}. ${liveCall.text}`,
    votes: summary.bits,
  };
}

function categoryTallies(rows) {
  const buckets = new Map();
  (rows || []).forEach((row) => {
    (row.votes || []).forEach((vote) => {
      const name = String(vote.category || "").trim();
      if (!name) return;
      if (!buckets.has(name)) buckets.set(name, { Buy: 0, Sell: 0, Hold: 0 });
      const mix = buckets.get(name);
      if (vote.side === "Buy" || vote.side === "Sell" || vote.side === "Hold") mix[vote.side] += 1;
    });
  });
  return [...buckets.entries()].map(
    ([name, mix]) => `${name}: ${mix.Buy} Buy, ${mix.Sell} Sell, ${mix.Hold} Hold.`,
  );
}

function mixFromSides(rows) {
  const buy = rows.filter((row) => row.side === "Buy").length;
  const sell = rows.filter((row) => row.side === "Sell").length;
  return { buy, sell, hold: rows.length - buy - sell };
}

function bookRank(row) {
  if (row.side === "Buy") return 0;
  if (row.side === "Sell") return 1;
  if (row.live === "Buy" || row.live === "Sell") return 2;
  return 3;
}

function coinRows(rows) {
  return [...rows]
    .sort((a, b) => bookRank(a) - bookRank(b) || b.strength - a.strength)
    .slice(0, 16)
    .map((row) => ({
      symbol: row.coin,
      side: row.side || "Hold",
      strength: Number(row.strength) || 0,
      live: row.live || "",
      size: Number.isFinite(row.size) ? row.size : null,
    }));
}

export function rowsFromSuggestions(suggestions, coefficients) {
  return (suggestions || [])
    .map((item) => {
      const call = assistantCall(item);
      return {
        coin: base(item?.SYMBOL_NAME),
        side: call.side || "Hold",
        strength: Number(item?.SIGNAL_STRENGTH) || 0,
        text: `${base(item?.SYMBOL_NAME)}: the AI Assistant says ${call.text}.`,
      };
    })
    .filter((row) => row.coin);
}

export function buildGuidePayload(state, question, extras) {
  const page = pageKind(
    typeof window !== "undefined" ? window.location.pathname : "",
  );
  const assistant = state?.tradingAssistant?.tradeAssistant?.ASSISTANT_SUGGESTIONS || [];
  const coefficients = state?.tradingAssistant?.tradeAssistant?.coefficients || {};
  const suggestions = state?.liveSuggestion?.suggestions || [];
  const timeframe = normalizeLiveTimeframe(state?.liveSuggestion?.liveTimeframe);
  const insights = state?.newInsightState?.signalData || [];
  const assistantOpen = state?.profile?.profileData?.myais_active === "True";
  const insightOpen = state?.profile?.profileData?.insai_active === "True";
  const asked = coinsInQuestion(question);
  const scope = questionScope(question);
  const ranking = scope.general;
  const openMarket =
    page === "insight"
      ? state?.newInsightState?.insightMarketType || "crypto"
      : state?.tradingAssistant?.aiMarketType || "crypto";
  const openMode = state?.tradingAssistant?.aiAssistantMode || "null";
  const cardTimeframe = state?.tradingAssistant?.selectedTimeFrame || "1h";
  const loadedFrames = new Set(
    (suggestions || []).map((item) => normalizeLiveTimeframe(item?.timeframe)),
  );
  const longLoaded = loadedFrames.has("1d") || loadedFrames.has("1w");
  const misses = [];
  const comparisons = Array.isArray(extras) ? extras : [];
  comparisons
    .filter((item) => item?.missing)
    .forEach((item) => misses.push(`${item.label} is not loaded.`));

  if (page === "insight") {
    const otherMarkets = otherMarketLines(question, openMarket, "insight");
    otherMarkets.forEach((item) => misses.push(item));
    const rows =
      insights.length && insightOpen
        ? insights
            .filter((item) => item?.coin)
            .map((item) => insightLine(item, suggestions, timeframe))
        : [];
    const focus = asked.length
      ? rows.filter((row) => asked.includes(row.coin))
      : rows.slice(0, 3);
    const lines = [
      `Insights pack: ${marketLabel(openMarket)}. Use the open strategy's indicator votes below.`,
    ];
    if (!rows.length) {
      lines.push(
        insightOpen
          ? "No Insights coins are loaded on this screen."
          : "Insights is not active, so those indicator calls stay hidden.",
      );
    } else {
      focus.forEach((row) => lines.push(row.text));
      const tallies = categoryTallies(focus);
      if (tallies.length) {
        lines.push("Category votes on the coins above:");
        tallies.forEach((tally) => lines.push(tally));
      }
    }
    asked
      .filter((coin) => !rows.some((row) => row.coin === coin))
      .forEach((coin) => lines.push(`${coin} is not on the open Insights pack.`));
    misses.forEach((miss) => lines.push(miss));
    const board = {
      page: "insight",
      market: openMarket,
      mode: "insight",
      mix: mixFromSides(rows),
      coins: coinRows(rows),
      misses,
      comparisons: [],
    };
    return { signals: lines.join("\n").slice(0, 6000), board };
  }

  const lines = [
    `The card timeframe is ${cardTimeframe}. The pivot timeframe selected on screen is ${timeframe}.`,
    `Open market: ${marketLabel(openMarket)}. Open mode: ${modeLabel(openMode)}.`,
  ];
  const horizon = horizonNote(question);
  if (horizon) lines.push(horizon);
  if (scope.longHold && !longLoaded) {
    lines.push("The 1d and 1w pivot checks are not loaded on this screen.");
  }
  questionScope(question).markets
    .filter((market) => market !== openMarket)
    .forEach((market) => {
      const name = marketLabel(market);
      const loaded = comparisons.some(
        (item) => !item?.missing && String(item.label || "").toLowerCase().startsWith(name),
      );
      if (loaded) return;
      lines.push(`The ${name} board did not load, so this answer cannot quote its calls.`);
    });
  const known = new Set();
  let rows = [];

  if (assistant.length && assistantOpen) {
    rows = assistant
      .map((item) => assistantLine(item, suggestions, timeframe, coefficients, cardTimeframe))
      .filter((row) => row.coin);
    rows.forEach((row) => known.add(row.coin));
    const mix = mixFromSides(rows);
    lines.push(
      `AI Assistant board: ${mix.buy} Buy, ${mix.sell} Sell, ${mix.hold} Hold, across ${rows.length} coins.`,
    );
    const focus = asked.length
      ? rows.filter((row) => asked.includes(row.coin))
      : ranking
        ? rows
        : [...rows].sort((a, b) => b.strength - a.strength).slice(0, 3);
    focus.forEach((row) => lines.push(row.text));
    const rest = rows
      .filter((row) => !focus.includes(row))
      .slice(0, 12)
      .map((row) => `${row.coin} ${row.side || "Hold"}`);
    if (rest.length) lines.push(`Other AI Assistant coins: ${rest.join(", ")}.`);
  } else if (assistant.length) {
    lines.push("AI Assistant is not active, so those signal numbers stay hidden.");
  }

  asked
    .filter((coin) => !known.has(coin))
    .forEach((coin) => {
      lines.push(`${coin} is not on the board loaded in this chat.`);
    });
  comparisons
    .filter((item) => !item?.missing)
    .forEach((item) => {
      const mix = item.mix || {};
      const names = (item.coins || [])
        .slice(0, 4)
        .map((coin) => `${coin.symbol} ${coin.side}`)
        .join(", ");
      lines.push(
        `Cached ${item.label}: ${mix.buy || 0} Buy, ${mix.sell || 0} Sell, ${mix.hold || 0} Hold.${names ? ` ${names}.` : ""}`,
      );
    });
  misses.forEach((miss) => lines.push(miss));
  if (lines.length === 2) lines.push("No signal board is loaded on this screen.");

  const board = {
    page: "assistant",
    market: openMarket,
    mode: openMode,
    longHold: scope.longHold,
    longLoaded,
    mix: mixFromSides(rows),
    coins: coinRows(rows),
    misses,
    comparisons: comparisons.filter((item) => !item?.missing),
  };
  return { signals: lines.join("\n").slice(0, 6000), board };
}

export function buildSignalBrief(state, question) {
  return buildGuidePayload(state, question).signals;
}
